import { useCallback, useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';

import type { OutstandingWeek, TimesheetPeriod } from '@time-tracking/shared';

import { PeriodStatusPill, type UiStatus } from './PeriodStatusPill.js';
import { StatusPill } from './StatusPill.js';
import { formatWeekRange } from '../utils/weekDates.js';

/**
 * Stable id of the week-range heading. After a popover row navigates, the page
 * moves focus here — `PeriodBar` unmounts while the new week loads and the
 * activated row disappears with it, so without an explicit destination focus
 * would land on `<body>`.
 */
export const WEEK_RANGE_HEADING_ID = 'timesheet-week-range';

// The popover's count heading labels the group; its footer instruction is what
// the arrow points `aria-describedby` at. Pointing the description at the
// heading instead would make a screen reader read the count twice back-to-back
// on focus, since the arrow's own accessible name already carries it.
const OUTSTANDING_HEADING_ID = 'outstanding-weeks-heading';
const OUTSTANDING_INSTRUCTION_ID = 'outstanding-weeks-instruction';

/** Rows rendered before the tail rolls up into "+N more". Handoff: `OUTSTANDING_MAX`. */
const OUTSTANDING_MAX = 4;

/**
 * Highest numeral the badge shows; above this it reads "9+". Purely visual —
 * the arrow's accessible name always carries the exact count.
 */
const BADGE_NUMERAL_CAP = 9;

export interface PeriodBarProps {
  period: TimesheetPeriod;
  status: UiStatus;
  onPrev: () => void;
  onNext: () => void;
  disableNext: boolean;
  /**
   * Weeks strictly earlier than the week on screen that still need action,
   * most-recent-first. Capped server-side, then re-filtered by the caller
   * relative to the *viewed* week — so this list shrinks as the contributor
   * walks backwards through the backlog. Only the first `OUTSTANDING_MAX` are
   * rendered.
   */
  outstandingWeeks?: OutstandingWeek[];
  /**
   * The *uncapped* number of outstanding weeks. Drives the badge numeral, the
   * accessible name, and the overflow line — all of which must stay honest even
   * though `outstandingWeeks` is truncated.
   */
  outstandingTotal?: number;
  /**
   * Run the finite attention pulse. The page owns this so it can replay the
   * pulse only for a week new to the session; `PeriodBar` unmounts on every
   * navigation and so cannot decide it itself.
   */
  showPulse?: boolean;
  /**
   * Render the nudge at all — tint, border, badge and popover. Separate from
   * `showPulse` because on a backlog week the pulse quiets while the badge and
   * popover stay reachable, so the user can walk to the next outstanding week.
   */
  showPopover?: boolean;
  /**
   * Navigate to a week the popover lists. Activating a row closes the popover
   * and calls this; the page owns moving focus afterwards.
   */
  onSelectWeek?: (weekStartDate: string) => void;
}

export function PeriodBar({
  period,
  status,
  onPrev,
  onNext,
  disableNext,
  outstandingWeeks = [],
  outstandingTotal = 0,
  showPulse = false,
  showPopover = false,
  onSelectWeek,
}: PeriodBarProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // The popover is revealed by CSS (`.arrow-wrap:hover, :focus-within`), not by
  // React. Keeping the reveal in CSS is what lets `element.focus()` open it from
  // a script — `:hover` cannot be triggered from JavaScript, so a JS-toggled
  // disclosure would cost the fidelity harness its only way to capture the open
  // state. These flags mirror that CSS condition for two jobs only: knowing when
  // to attach the document-level Escape listener, and applying the dismissal.
  const [pointerInside, setPointerInside] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const nudging = showPopover && outstandingTotal > 0;
  const revealed = nudging && (pointerInside || focusInside) && !dismissed;

  // Escape-to-dismiss (WCAG 1.4.13). The listener is on `document`, not on the
  // wrapper: a wrapper-bound `keydown` only fires when focus is already inside
  // the wrapper, so the primary case — a mouse user who hovered while focus sits
  // in a grid cell — would get nothing, leaving the very SC this is here to
  // satisfy unmet.
  useEffect(() => {
    if (!revealed) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDismissed(true);
      // Only reclaim focus if it was inside the popover we are about to hide;
      // otherwise leave the user's focus exactly where they left it.
      const active = document.activeElement;
      if (active instanceof Node && popoverRef.current?.contains(active)) {
        arrowRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [revealed]);

  const handlePointerEnter = useCallback(() => setPointerInside(true), []);

  // Dismissal is per-reveal, not permanent: leaving the wrapper ends the reveal,
  // so the flag clears with it and the next hover starts fresh. It is cleared
  // *only* here and on a focusout that leaves the wrapper — never when the arrow
  // itself gains focus, because Escape's own focus return would then immediately
  // re-satisfy `:focus-within` and re-reveal the popover.
  const handlePointerLeave = useCallback(() => {
    setPointerInside(false);
    setDismissed(false);
  }, []);

  const handleFocus = useCallback(() => setFocusInside(true), []);

  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && wrapRef.current?.contains(next)) return;
    setFocusInside(false);
    setDismissed(false);
  }, []);

  const handleSelectWeek = useCallback(
    (weekStartDate: string) => {
      // Close this reveal, then hand navigation to the page. The activated row
      // is about to be hidden, so the page owns the focus destination.
      setDismissed(true);
      onSelectWeek?.(weekStartDate);
    },
    [onSelectWeek],
  );

  // Grammatical singular/plural. The handoff's aria-label template renders
  // "1 earlier timesheet still need attention"; fixed here. Its popover *header*
  // template pluralizes correctly and is reproduced unchanged below.
  const plural = outstandingTotal === 1 ? '' : 's';
  const verbS = outstandingTotal === 1 ? 's' : '';
  const arrowLabel = nudging
    ? `Previous week — ${outstandingTotal} earlier timesheet${plural} still need${verbS} attention`
    : 'Previous week';

  const badgeText =
    outstandingTotal > BADGE_NUMERAL_CAP ? `${BADGE_NUMERAL_CAP}+` : String(outstandingTotal);

  // Rows are most-recent-first, so the truncated tail is the *oldest* weeks —
  // which is why the overflow line reads "oldest first" even though the visible
  // rows are newest-first. It reads contradictory at a glance; it isn't.
  //
  // Counted off the rows actually rendered, not off OUTSTANDING_MAX: the list
  // can be *shorter* than the cap while `outstandingTotal` is larger than it
  // (the server caps rows at 20 but never the total, and the page re-filters the
  // rows relative to the viewed week). Subtracting the constant there would
  // print an overflow that does not reconcile with the heading — e.g. one row
  // and "+2 more" under a "6 … need attention" heading.
  const visibleWeeks = outstandingWeeks.slice(0, OUTSTANDING_MAX);
  const overflowCount = outstandingTotal - visibleWeeks.length;

  return (
    <div className="period-bar" role="region" aria-label="Timesheet period">
      <nav className="period-nav" aria-label="Week navigation">
        <div
          ref={wrapRef}
          className={`arrow-wrap${nudging ? ' has-outstanding' : ''}`}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          <button
            ref={arrowRef}
            className={`arrow${nudging ? ' has-outstanding' : ''}${
              nudging && showPulse ? ' pulsing' : ''
            }`}
            type="button"
            onClick={onPrev}
            aria-label={arrowLabel}
            {...(nudging ? { 'aria-describedby': OUTSTANDING_INSTRUCTION_ID } : {})}
            data-testid="period-prev-arrow"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M10 12L6 8l4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {/* The exact count already rides in the arrow's accessible name, so
                announcing the badge as well would just be noise. */}
            {nudging && (
              <span
                className="outstanding-badge"
                aria-hidden="true"
                data-testid="outstanding-badge"
              >
                {badgeText}
              </span>
            )}
          </button>
          {nudging && (
            /* A labelled group, deliberately NOT role="tooltip": a tooltip must
               not contain interactive content, which would put these week
               buttons out of reach for keyboard and screen-reader users. That
               exact anti-pattern is a documented past bug in this repo
               (docs/solutions/ui-bugs/interactive-tr-no-aria-role-missing-focus-trap-2026-05-07.md). */
            <div
              ref={popoverRef}
              className="outstanding-pop"
              role="group"
              aria-labelledby={OUTSTANDING_HEADING_ID}
              hidden={dismissed}
              data-testid="outstanding-popover"
            >
              <div className="op-head" id={OUTSTANDING_HEADING_ID}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
                <span>
                  {`${outstandingTotal} earlier timesheet${plural} need${verbS} attention`}
                </span>
              </div>
              <div className="op-list">
                {visibleWeeks.map((week) => (
                  /* A real button, so the row's accessible name is the week
                     range plus the pill's status label — nothing here depends
                     on being able to see the pill. */
                  <button
                    key={week.weekStartDate}
                    type="button"
                    className="op-row"
                    onClick={() => handleSelectWeek(week.weekStartDate)}
                    data-testid="outstanding-week-row"
                  >
                    <span className="op-range">
                      {formatWeekRange(week.weekStartDate, week.weekEndDate)}
                    </span>
                    <StatusPill status={week.status} />
                  </button>
                ))}
              </div>
              {overflowCount > 0 && (
                <div className="op-more">+{overflowCount} more, oldest first</div>
              )}
              {/* Softened from the handoff's "Go back to finish and submit
                  them." — a week under the weekly minute target is hard-rejected
                  with a 422, so promising submission would instruct an action
                  the API refuses. */}
              <div className="op-foot" id={OUTSTANDING_INSTRUCTION_ID}>
                Go back to any week to pick up where you left off.
              </div>
            </div>
          )}
        </div>
        <div className="period-label">
          {/* A native <h2>, not a span with role="heading" + aria-level: same
              semantics with no ARIA to keep in sync, and it sits correctly one
              level under the page's <h1>Timesheet</h1>. The stable id and
              tabIndex={-1} exist purely so the page has something meaningful to
              focus after a popover row navigates. `.period-label .big` in
              globals.css keys off the class, not the tag, so the styling is
              unchanged apart from the `margin: 0` added there to drop the UA
              heading margin. */}
          <h2 className="big" id={WEEK_RANGE_HEADING_ID} tabIndex={-1}>
            {formatWeekRange(period.weekStartDate, period.weekEndDate)}
          </h2>
        </div>
        <button
          className="arrow"
          type="button"
          onClick={onNext}
          disabled={disableNext}
          aria-label="Next week"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </nav>
      <div className="period-status">
        <PeriodStatusPill status={status} />
      </div>
    </div>
  );
}
